import Component from 'flarum/common/Component';
import Button from 'flarum/common/components/Button';
import app from 'flarum/forum/app';
import RsvpModal from './RsvpModal';
import ParticipantsList from './ParticipantsList';
import SectionSummary from './SectionSummary';
import EventFormModal from './EventFormModal';
import sectionName from '../utils/sections';

export default class EventBanner extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    this.loading = false;
  }

  view() {
    const discussion = this.attrs.discussion;
    const event = discussion.event();

    if (!event) return null;

    const eventDate = new Date(event.eventDate());
    const eventEndDate = event.eventEndDate() ? new Date(event.eventEndDate()) : null;

    // Calculate if event is past (for display purposes):
    // - If end_date exists: compare end_date with today
    // - If no end_date: compare event_date (date only) with today
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let isPast;
    if (eventEndDate) {
      isPast = eventEndDate < today;
    } else {
      const eventDateOnly = new Date(eventDate);
      eventDateOnly.setHours(0, 0, 0, 0);
      isPast = eventDateOnly < today;
    }

    // Can only RSVP if start date hasn't passed yet and event is not cancelled
    const now = new Date();
    const eventStatus = event.eventStatus() || 'scheduled';
    const canStillRsvp = eventDate > now && eventStatus !== 'cancelled';

    const userRsvp = this.getUserRsvp(event);

    return (
      <div className={`EventBanner ${isPast ? 'EventBanner--past' : ''}`}>
        {event.isPrivate() && (
          <div className="EventBanner-privateBadge">
            <i className="fas fa-lock"></i>
            {' '}{app.translator.trans('florcarioca-rsvp.forum.event.private')}
          </div>
        )}
        {eventStatus === 'scheduled' && (
          <div className="EventBanner-statusBadge EventBanner-statusBadge--scheduled">
            <i className="fas fa-clock"></i>
            {' '}{app.translator.trans('florcarioca-rsvp.forum.event.status_scheduled')}
          </div>
        )}
        {eventStatus === 'confirmed' && (
          <div className="EventBanner-statusBadge EventBanner-statusBadge--confirmed">
            <i className="fas fa-check-double"></i>
            {' '}{app.translator.trans('florcarioca-rsvp.forum.event.status_confirmed')}
          </div>
        )}
        {eventStatus === 'cancelled' && (
          <div className="EventBanner-statusBadge EventBanner-statusBadge--cancelled">
            <i className="fas fa-ban"></i>
            {' '}{app.translator.trans('florcarioca-rsvp.forum.event.status_cancelled')}
          </div>
        )}
        <div className="EventBanner-header">
          <div className="EventBanner-date">
            <i className="fas fa-calendar-alt"></i>
            <span className="EventBanner-dateText">
              {this.formatDate(eventDate)}
              {eventEndDate && (
                <span> → {this.isSameDay(eventDate, eventEndDate) ? this.formatTime(eventEndDate) : this.formatDate(eventEndDate)}</span>
              )}
            </span>
          </div>
          {event.location() && (
            <div className="EventBanner-location">
              <i className="fas fa-map-marker-alt"></i>
              <span>{event.location()}</span>
            </div>
          )}
        </div>

        {event.description() && (
          <div className="EventBanner-description">
            {event.description()}
          </div>
        )}

        <div className="EventBanner-stats">
          <span className="EventBanner-stat EventBanner-stat--attending">
            <i className="fas fa-check-circle"></i>
            {event.attendeeCount()} {app.translator.trans('florcarioca-rsvp.forum.event.attending')}
          </span>
          <span className="EventBanner-stat EventBanner-stat--not-attending">
            <i className="fas fa-times-circle"></i>
            {event.notAttendingCount()} {app.translator.trans('florcarioca-rsvp.forum.event.not_attending')}
          </span>
          {event.totalMembersCount() > 0 && (
            <span className="EventBanner-stat EventBanner-stat--no-response">
              <i className="fas fa-question-circle"></i>
              {event.noResponseCount()} {app.translator.trans('florcarioca-rsvp.forum.event.no_response')}
            </span>
          )}
        </div>

        <SectionSummary event={event} />

        {canStillRsvp && event.canRsvp() && (
          <div className="EventBanner-actions">
            {this.renderRsvpButtons(event, userRsvp)}
          </div>
        )}

        {userRsvp && (
          <div className={`EventBanner-myRsvp EventBanner-myRsvp--${userRsvp.status()}`}>
            <i className={this.getStatusIcon(userRsvp.status())}></i>
            {app.translator.trans(`florcarioca-rsvp.forum.event.status.${userRsvp.status()}`)}
            {userRsvp.section() && (
              <span className="EventBanner-mySection">
                ({this.getSectionName(userRsvp.section())})
              </span>
            )}
          </div>
        )}

        <ParticipantsList event={event} />

        {(event.canEdit() || event.canExport()) && (
          <div className="EventBanner-adminActions">
            {event.canEdit() && (
              <Button
                className="Button Button--link"
                icon="fas fa-edit"
                onclick={() => this.openEditModal(event)}
              >
                {app.translator.trans('florcarioca-rsvp.forum.event.btn_edit')}
              </Button>
            )}
            {event.canExport() && (
              <Button
                className="Button Button--link"
                icon="fas fa-file-csv"
                onclick={() => this.exportCsv(event)}
              >
                {app.translator.trans('florcarioca-rsvp.forum.event.export_csv')}
              </Button>
            )}
          </div>
        )}
      </div>
    );
  }

  renderRsvpButtons(event, userRsvp) {
    const currentStatus = userRsvp ? userRsvp.status() : null;

    return (
      <div className="ButtonGroup">
        <Button
          className={`Button ${currentStatus === 'attending' ? 'Button--primary active' : ''}`}
          icon="fas fa-check"
          onclick={() => this.openRsvpModal(event, 'attending')}
          loading={this.loading}
        >
          {app.translator.trans('florcarioca-rsvp.forum.event.btn_attending')}
        </Button>
        <Button
          className={`Button ${currentStatus === 'not_attending' ? 'Button--danger active' : ''}`}
          icon="fas fa-times"
          onclick={() => this.submitRsvp(event, 'not_attending', null, null)}
          loading={this.loading}
        >
          {app.translator.trans('florcarioca-rsvp.forum.event.btn_not_attending')}
        </Button>
      </div>
    );
  }

  openRsvpModal(event, status) {
    app.modal.show(RsvpModal, {
      event,
      status,
      onsubmit: (section, comment) => this.submitRsvp(event, status, section, comment),
    });
  }

  async submitRsvp(event, status, section, comment) {
    this.loading = true;
    m.redraw();

    try {
      await app.request({
        method: 'POST',
        url: `${app.forum.attribute('apiUrl')}/events/${event.id()}/rsvp`,
        body: {
          data: {
            attributes: {
              status,
              section,
              comment,
            },
          },
        },
      });

      // Reload the discussion to get updated RSVP data
      await app.store.find('discussions', this.attrs.discussion.id());
      app.modal.close();
    } catch (e) {
      app.alerts.show({ type: 'error' }, e.message || 'Une erreur est survenue');
    }

    this.loading = false;
    m.redraw();
  }

  getUserRsvp(event) {
    if (!app.session.user) return null;
    const rsvps = event.rsvps() || [];
    return rsvps.find((r) => r.user() && r.user().id() === app.session.user.id());
  }

  formatDate(date) {
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  formatTime(date) {
    return date.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  isSameDay(date1, date2) {
    return date1.getFullYear() === date2.getFullYear() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getDate() === date2.getDate();
  }

  getStatusIcon(status) {
    const icons = {
      attending: 'fas fa-check-circle',
      not_attending: 'fas fa-times-circle',
    };
    return icons[status] || 'fas fa-question-circle';
  }

  getSectionName(sectionId) {
    const sections = JSON.parse(app.forum.attribute('florCariocaSections') || '[]');
    const section = sections.find((s) => s.id === sectionId);
    return section ? sectionName(section) : sectionId;
  }

  exportCsv(event) {
    window.open(`${app.forum.attribute('apiUrl')}/events/${event.id()}/export`, '_blank');
  }

  openEditModal(event) {
    app.modal.show(EventFormModal, {
      event,
      discussion: this.attrs.discussion,
    });
  }
}
